/**
 * ODM Submission Detail — full lifecycle drill-down for a single 837P batch.
 *
 * Thin orchestrator. Logic lives in:
 *   - useSubmissionDetail (data fetch + notify-list updater)
 *   - TimelineItem, ResubmitCard, NotifyMeCard, ResponseCard,
 *     ClaimsTable + BatchMetadataCard (presentation)
 *   - constants.js (formatters + STATUS_TONE + buildTimelineWithPending)
 */
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { StatsCardGrid } from '../components/shared/StatsCardGrid';
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  XCircle,
  CurrencyDollar,
  Stack,
  Users,
} from '@phosphor-icons/react';
import {
  buildTimelineWithPending,
  STATUS_BADGE_CLASS,
} from './odm-submission-detail/constants';
import { useSubmissionDetail } from './odm-submission-detail/useSubmissionDetail';
import TimelineItem from './odm-submission-detail/TimelineItem';
import ResubmitCard from './odm-submission-detail/ResubmitCard';
import NotifyMeCard from './odm-submission-detail/NotifyMeCard';
import ResponseCard from './odm-submission-detail/ResponseCard';
import {
  ClaimsTable,
  BatchMetadataCard,
} from './odm-submission-detail/ClaimsTableAndMeta';

const LoadingView = () => (
  <Layout>
    <div className="flex items-center justify-center py-24" data-testid="odm-detail-loading">
      <div className="text-slate-400">Loading submission details…</div>
    </div>
  </Layout>
);

const ErrorView = ({ error, onBack }) => (
  <Layout>
    <div className="space-y-4 max-w-xl mx-auto py-12 text-center" data-testid="odm-detail-error">
      <XCircle size={48} className="mx-auto text-red-500" weight="duotone" />
      <h2 className="text-lg font-semibold text-slate-900">
        {error || 'Submission not found'}
      </h2>
      <Button onClick={onBack} variant="outline">
        <ArrowLeft size={14} className="mr-1" />
        Back to history
      </Button>
    </div>
  </Layout>
);

const BatchHeader = ({ batch }) => {
  const navigate = useNavigate();
  return (
    <div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/odm/submissions')}
        className="mb-2 text-slate-600 -ml-2"
        data-testid="odm-detail-back-btn"
      >
        <ArrowLeft size={14} className="mr-1" />
        Back to submission history
      </Button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-slate-900 truncate">
            Batch {batch.batch_id}
          </h1>
          <p
            className="font-mono text-xs text-slate-500 mt-1 truncate"
            title={batch.filename}
          >
            {batch.filename}
          </p>
        </div>
        <Badge
          className={`text-xs px-3 py-1 ${STATUS_BADGE_CLASS(batch.response_status)}`}
          data-testid="odm-detail-status-badge"
        >
          {batch.response_status}
        </Badge>
      </div>
      {(batch.parent_batch_id || batch.resubmitted_as_batch_id) && (
        <div
          className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600"
          data-testid="odm-detail-lineage"
        >
          {batch.parent_batch_id && (
            <button
              type="button"
              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 transition-colors"
              onClick={() => navigate(`/odm/submissions/${batch.parent_batch_id}`)}
            >
              <ArrowLeft size={11} />
              Resubmit of {batch.parent_batch_id}
            </button>
          )}
          {batch.resubmitted_as_batch_id && (
            <button
              type="button"
              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200 transition-colors"
              onClick={() =>
                navigate(`/odm/submissions/${batch.resubmitted_as_batch_id}`)
              }
              data-testid="odm-detail-child-link"
            >
              Resubmitted as {batch.resubmitted_as_batch_id} →
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const buildStatsCards = (stats) => [
  {
    label: 'Claims in batch',
    value: stats.total_claims,
    icon: Users,
    color: 'indigo',
    testid: 'stat-claims-in-batch',
  },
  {
    label: 'Total charged',
    value: stats.total_charged,
    icon: CurrencyDollar,
    color: 'sky',
    isCurrency: true,
    testid: 'stat-total-charged',
  },
  {
    label: 'Total paid',
    value: stats.total_paid,
    icon: CurrencyDollar,
    color: 'emerald',
    isCurrency: true,
    testid: 'stat-total-paid',
  },
  {
    label: 'Accepted (999)',
    value: stats.accepted,
    icon: CheckCircle,
    color: 'emerald',
    testid: 'stat-accepted-999',
  },
  {
    label: 'Rejected (999)',
    value: stats.rejected,
    icon: XCircle,
    color: 'red',
    testid: 'stat-rejected-999',
  },
];

const NoResponsesYet = () => (
  <Card>
    <CardContent className="py-8 text-center">
      <Clock size={32} className="mx-auto text-slate-300 mb-2" weight="duotone" />
      <p className="text-sm font-medium text-slate-700">No responses received yet</p>
      <p className="text-xs text-slate-500 mt-1">
        ODM CERT typically responds within 1–4 hours. The poller checks every 30 minutes;
        click "Check now" on the Claims page to refresh manually.
      </p>
    </CardContent>
  </Card>
);

const ResponseDetails = ({ responses }) => {
  const isEmpty = Object.keys(responses || {}).every(
    (k) => !responses[k] || responses[k].length === 0
  );
  if (isEmpty) return <NoResponsesYet />;
  return (
    <>
      <ResponseCard ftype="TA1" items={responses?.TA1} />
      <ResponseCard ftype="999" items={responses?.['999']} />
      <ResponseCard ftype="ErrorReport" items={responses?.ErrorReport} />
      <ResponseCard ftype="824" items={responses?.['824']} />
      <ResponseCard ftype="277" items={responses?.['277']} />
      <ResponseCard ftype="835" items={responses?.['835']} />
    </>
  );
};

export default function OdmSubmissionDetailPage() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, loading, error, updateNotifyEmails } = useSubmissionDetail(batchId);

  if (loading) return <LoadingView />;
  if (error || !data) {
    return <ErrorView error={error} onBack={() => navigate('/odm/submissions')} />;
  }

  const { batch, timeline, responses, claims, stats } = data;
  const fullTimeline = buildTimelineWithPending(timeline || []);
  const hasRejection = (batch.response_status || '').toLowerCase().includes('reject');
  const statsCards = buildStatsCards(stats);

  return (
    <Layout>
      <div className="space-y-6" data-testid="odm-submission-detail-page">
        <BatchHeader batch={batch} />

        <StatsCardGrid columns={5} cards={statsCards} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <NotifyMeCard
            batchId={batch.batch_id}
            notifyEmails={batch.notify_emails || []}
            currentUserEmail={user?.email}
            onChange={updateNotifyEmails}
          />
          <ResubmitCard batch={batch} hasRejection={hasRejection} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-1" data-testid="odm-detail-timeline-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Stack size={18} className="text-indigo-600" />
                Lifecycle
              </CardTitle>
              <CardDescription className="text-xs">
                Each milestone confirms a step ODM completed
              </CardDescription>
            </CardHeader>
            <CardContent>
              {fullTimeline.map((item, idx) => (
                <TimelineItem
                  key={`${item.milestone}-${item.timestamp || idx}`}
                  item={item}
                  isLast={idx === fullTimeline.length - 1}
                />
              ))}
            </CardContent>
          </Card>

          <div className="lg:col-span-2 space-y-4">
            <ResponseDetails responses={responses} />
          </div>
        </div>

        <ClaimsTable claims={claims} />
        <BatchMetadataCard batch={batch} />
      </div>
    </Layout>
  );
}
